import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import subscriptionService from '../services/subscriptionService';

interface UpgradePromptProps {
  open: boolean;
  onClose: () => void;
  subject: string;
  remainingTests: number | string;
  onUpgradeSuccess?: () => void;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  open,
  onClose,
  subject,
  remainingTests,
  onUpgradeSuccess
}) => {
  const theme = useTheme();
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async (planType: string) => {
    try {
      setProcessingPayment(planType);
      setError(null);

      const paymentSuccess = await subscriptionService.processPayment(planType);
      
      if (paymentSuccess) {
        if (onUpgradeSuccess) {
          onUpgradeSuccess();
        }
        onClose();
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      if (error.message === 'Payment cancelled by user') {
        setError('Payment was cancelled');
      } else {
        setError('Payment failed. Please try again.');
      }
    } finally {
      setProcessingPayment(null);
    }
  };

  const subjectDisplayName = subject.charAt(0).toUpperCase() + subject.slice(1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <LockIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="h5" component="div" fontWeight="bold">
              Test Limit Reached
            </Typography>
          </Box>
          <Button onClick={onClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box textAlign="center" mb={3}>
          <Typography variant="h6" color="text.secondary" mb={2}>
            You've used all your free {subjectDisplayName} tests!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upgrade to unlimited access and unlock all premium features
          </Typography>
        </Box>

        {/* Non-auto-renewal notice */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="info.dark" sx={{ fontWeight: 500, mb: 0.5 }}>
            📅 One-time Payment • No Auto-renewal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Pay once, use for the full duration. Manual renewal required after expiry.
          </Typography>
        </Box>

        {error && (
          <Box mb={2}>
            <Typography color="error" textAlign="center">
              {error}
            </Typography>
          </Box>
        )}

        {/* Upgrade Options */}
        <Box display="flex" gap={2} mb={3}>
          {/* Monthly Plan */}
          <Card 
            sx={{ 
              flex: 1,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <StarIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Monthly Plan
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold" mb={1}>
                ₹200
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                per month
              </Typography>
              
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => handleUpgrade('monthly')}
                disabled={processingPayment !== null}
                sx={{ mb: 2 }}
              >
                {processingPayment === 'monthly' ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  'Choose Monthly'
                )}
              </Button>

              <Typography variant="body2" color="text.secondary">
                Perfect for short-term preparation
              </Typography>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card 
            sx={{ 
              flex: 1,
              border: `2px solid ${theme.palette.warning.main}`,
              position: 'relative',
              '&:hover': {
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <Chip
              label="Best Value"
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: '#f59e0b',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            
            <CardContent sx={{ textAlign: 'center', p: 3, pt: 4 }}>
              <TrophyIcon sx={{ color: '#f59e0b', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Yearly Plan
              </Typography>
              <Typography variant="h4" sx={{ color: '#f59e0b' }} fontWeight="bold" mb={1}>
                ₹2000
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                per year
              </Typography>
              <Typography variant="body2" color="success.main" fontWeight="bold" mb={2}>
                Save ₹400!
              </Typography>
              
              <Button
                fullWidth
                variant="contained"
                sx={{ 
                  bgcolor: '#f59e0b',
                  '&:hover': { bgcolor: '#d97706' },
                  mb: 2
                }}
                onClick={() => handleUpgrade('yearly')}
                disabled={processingPayment !== null}
              >
                {processingPayment === 'yearly' ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  'Choose Yearly'
                )}
              </Button>

              <Typography variant="body2" color="text.secondary">
                Best for complete exam preparation
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Features */}
        <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} textAlign="center">
              What you'll get with premium:
            </Typography>
            <List dense>
              {[
                'Unlimited tests in all subjects',
                'Detailed performance analytics',
                'Progress tracking & insights',
                'No advertisements',
                'Priority customer support'
              ].map((feature, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Security Info */}
        <Box mt={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            🔒 Secure payments powered by Razorpay • Cancel anytime • No hidden fees
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePrompt; 